import { useEffect, useMemo, useState } from "react";
import { api } from "../../api.js";
import {
  Button, Eyebrow, Field, Notice, Panel, SearchCombobox, SelectField, TextAreaField,
} from "../../components/ui.jsx";
import { commaValues, DEFAULT_STAGES, FIELD_TYPES } from "./list-utils.js";

export function CreateListPanel({ onCreated, onCancel }) {
  const [form, setForm] = useState({ name: "", entity_type: "company", description: "", stages: DEFAULT_STAGES });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await api.createList({
        name: form.name,
        entity_type: form.entity_type,
        description: form.description || null,
        stages: commaValues(form.stages),
      });
      onCreated(data.list);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <form className="grid gap-5 md:grid-cols-2" onSubmit={submit}>
        <div className="col-span-full flex items-start justify-between gap-4">
          <div className="grid gap-1"><Eyebrow>New table</Eyebrow><h2 className="text-xl font-bold tracking-[-0.02em]">Create a focused list</h2></div>
          <Button variant="ghost" onClick={onCancel}>Close</Button>
        </div>
        {error && <div className="col-span-full"><Notice>{error}</Notice></div>}
        <Field label="List name" placeholder="Retail accounts" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoFocus required />
        <SelectField label="Primary records" value={form.entity_type} onChange={(event) => setForm({ ...form, entity_type: event.target.value })}>
          <option value="company">Companies</option><option value="person">People</option>
        </SelectField>
        <div className="col-span-full"><TextAreaField label="Description" hint="optional" placeholder="What belongs in this list?" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></div>
        <div className="col-span-full"><Field label="Workflow stages" hint="comma separated" value={form.stages} onChange={(event) => setForm({ ...form, stages: event.target.value })} required /></div>
        <div className="col-span-full flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Creating…" : "Create list"}</Button></div>
      </form>
    </Panel>
  );
}

export function EntityAdder({ list, onAdded, onCancel }) {
  const [state, setState] = useState({ loading: true, entities: [], error: "" });
  const [entityId, setEntityId] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const existingIds = useMemo(() => new Set(list.rows.map((row) => row.entity.id)), [list.rows]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setState((current) => ({ ...current, loading: true, error: "" }));
      const params = { limit: 100, ...(search.trim() ? { search: search.trim() } : {}) };
      const load = list.entity_type === "company" ? api.companies(params) : api.people(params);
      load.then((data) => setState({ loading: false, entities: data.companies || data.people || [], error: "" }))
        .catch((error) => setState({ loading: false, entities: [], error: error.message }));
    }, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [list.entity_type, search]);

  const available = state.entities.filter((entity) => !existingIds.has(entity.id));

  async function add() {
    if (!entityId) return;
    setBusy(true);
    try {
      await api.addListRow(list.id, { entity_id: entityId, stage: list.configuration.stages?.[0] || "new" });
      onAdded();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel className="grid gap-5">
      <div className="flex items-start justify-between gap-4"><div className="grid gap-1"><Eyebrow>Canonical records</Eyebrow><h2 className="text-xl font-bold tracking-[-0.02em]">Add {list.entity_type === "company" ? "a company" : "a person"}</h2></div><Button variant="ghost" onClick={onCancel}>Close</Button></div>
      <p className="text-[13px] leading-6 text-muted-foreground">The record is linked into this list—not copied—so edits stay consistent everywhere.</p>
      {state.error && <Notice>{state.error}</Notice>}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <SearchCombobox
          label={list.entity_type === "company" ? "Company" : "Person"}
          value={entityId}
          onChange={setEntityId}
          onSearchChange={(nextSearch) => { setSearch(nextSearch); setEntityId(""); }}
          options={available.map((entity) => ({ value: entity.id, label: entity.name, description: entity.domain || entity.title || entity.business_email || "Canonical record" }))}
          placeholder={`Type to search ${list.entity_type === "company" ? "companies" : "people"}…`}
          emptyMessage="No available matches. Try another search."
          loading={state.loading}
        />
        <Button variant="primary" disabled={!entityId || busy} onClick={add}>{busy ? "Adding…" : "Add to list"}</Button>
      </div>
    </Panel>
  );
}

export function ColumnBuilder({ list, onAdded, onCancel }) {
  const [level, setLevel] = useState("primary");
  const [scope, setScope] = useState("profile");
  const [fields, setFields] = useState([]);
  const [fieldId, setFieldId] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [form, setForm] = useState({ label: "", type: "text", options: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const targetType = level === "nested_person" ? "person" : list.entity_type;

  useEffect(() => {
    setFieldId("");
    api.fields(targetType, {
      scope,
      ...(scope === "list" ? { list_id: list.id } : {}),
    }).then((data) => setFields(data.fields)).catch((requestError) => setError(requestError.message));
  }, [list.id, scope, targetType]);

  const used = new Set(list.columns.filter((column) => column.entity_level === level).map((column) => column.field.id));
  const available = fields.filter((field) => !used.has(field.id));

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      let nextFieldId = fieldId;
      if (createMode) {
        const needsOptions = ["select", "multi_select"].includes(form.type);
        const data = await api.createField({
          entity_type: targetType,
          label: form.label,
          type: form.type,
          scope,
          ...(scope === "list" ? { list_id: list.id } : {}),
          ...(needsOptions ? { options: commaValues(form.options) } : {}),
        });
        nextFieldId = data.field.id;
      }
      if (!nextFieldId) throw new Error("Choose a field to add");
      await api.addListColumn(list.id, { field_id: nextFieldId, entity_level: level });
      onAdded();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <form className="grid gap-5 md:grid-cols-2" onSubmit={submit}>
        <div className="col-span-full flex items-start justify-between gap-4"><div className="grid gap-1"><Eyebrow>Table schema</Eyebrow><h2 className="text-xl font-bold tracking-[-0.02em]">Add a typed column</h2></div><Button variant="ghost" onClick={onCancel}>Close</Button></div>
        {error && <div className="col-span-full"><Notice>{error}</Notice></div>}
        {list.entity_type === "company" && <SelectField label="Column level" value={level} onChange={(event) => setLevel(event.target.value)}><option value="primary">Company row</option><option value="nested_person">People under company</option></SelectField>}
        <SelectField label="Value scope" value={scope} onChange={(event) => setScope(event.target.value)}>
          <option value="profile">Shared profile field</option>
          <option value="list">This list only</option>
        </SelectField>
        <p className="col-span-full text-xs leading-5 text-muted-foreground">
          {scope === "profile"
            ? "One value is shared everywhere this company or person appears."
            : `Values stay inside ${list.name} and can differ from every other list.`}
        </p>
        <SelectField label="Source" value={createMode ? "new" : "existing"} onChange={(event) => setCreateMode(event.target.value === "new")}><option value="existing">Existing field</option><option value="new">Define new field</option></SelectField>
        {createMode ? <>
          <Field label="Column label" placeholder={targetType === "company" ? "Employee count" : "Seniority"} value={form.label} onChange={(event) => setForm({ ...form, label: event.target.value })} required />
          <SelectField label="Field type" value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>{FIELD_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</SelectField>
          {["select", "multi_select"].includes(form.type) && <div className="col-span-full"><Field label="Options" hint="comma separated" placeholder="Small, Medium, Large" value={form.options} onChange={(event) => setForm({ ...form, options: event.target.value })} required /></div>}
        </> : <div className="col-span-full"><SelectField label={`Available ${targetType} fields`} value={fieldId} onChange={(event) => setFieldId(event.target.value)}><option value="">Select…</option>{available.map((field) => <option key={field.id} value={field.id}>{field.label} · {field.type.replace("_", " ")}</option>)}</SelectField>{!available.length && <p className="mt-2 text-xs text-muted-foreground">No unused {scope === "profile" ? "shared" : "list-only"} fields yet. Choose “Define new field”.</p>}</div>}
        <div className="col-span-full flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Adding…" : "Add column"}</Button></div>
      </form>
    </Panel>
  );
}

export function StageSettings({ list, onSaved, onCancel }) {
  const [stages, setStages] = useState((list.configuration.stages || []).join(", "));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.updateList(list.id, { stages: commaValues(stages) });
      onSaved();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel><form className="grid gap-5" onSubmit={submit}><div className="flex items-start justify-between gap-4"><div className="grid gap-1"><Eyebrow>List workflow</Eyebrow><h2 className="text-xl font-bold tracking-[-0.02em]">Configure stages</h2></div><Button variant="ghost" onClick={onCancel}>Close</Button></div>{error && <Notice>{error}</Notice>}<Field label="Stages" hint="comma separated and unique to this list" value={stages} onChange={(event) => setStages(event.target.value)} required /><div className="flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save stages"}</Button></div></form></Panel>
  );
}
